import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiDelete, apiGet, apiPost, apiPut, operationsClient } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { schedulerKeys } from '@/lib/queryKeys';

/** A single on-call shift. name + phone are denormalized on the backend, so the
 *  roster is self-contained (no technician join needed to render). */
export interface OnCallSchedule {
  id: string;
  technicianId: string;
  supervisorTechnicianId?: string | null;
  supervisorTechnician?: {
    id: string;
    name: string;
    email: string;
    phone?: string | null;
  } | null;
  name: string;
  phone: string;
  startDate: string; // ISO
  endDate: string; // ISO
  priority: number;
  acknowledgementTimeoutMinutes: number;
  notes?: string | null;
}

export interface OnCallInput {
  technicianId: string;
  supervisorTechnicianId?: string | null;
  name: string;
  phone: string;
  startDate: string;
  endDate: string;
  priority: number;
  acknowledgementTimeoutMinutes: number;
  notes?: string | null;
}

export interface EmergencyDispatch {
  id: string;
  status: 'PENDING' | 'ACKNOWLEDGED' | 'ESCALATED' | 'CANCELLED';
  acknowledgementDueAt: string;
  acknowledgedAt?: string | null;
  escalatedAt?: string | null;
  technician: { id: string; name: string };
  supervisorTechnician?: { id: string; name: string } | null;
  appointment: {
    id: string;
    customerName: string;
    address: string;
    startAt?: string | null;
    serviceType: { name: string };
  };
}

/** The full on-call roster (ordered by priority, then start). */
export function useOnCallSchedules() {
  return useApiQuery<OnCallSchedule[]>({
    queryKey: schedulerKeys.oncalls(),
    queryFn: () => apiGet<OnCallSchedule[]>(operationsClient, schedulerEndpoints.oncalls()),
    showErrorToast: false,
  });
}

export function useEmergencyDispatches() {
  return useApiQuery<EmergencyDispatch[]>({
    queryKey: [...schedulerKeys.oncalls(), 'emergencies'],
    queryFn: () =>
      apiGet<EmergencyDispatch[]>(operationsClient, schedulerEndpoints.emergencyDispatches()),
    showErrorToast: false,
    refetchInterval: 30_000,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: schedulerKeys.oncalls() });
}

export function useCreateOnCall() {
  const invalidate = useInvalidate();
  return useApiMutation<OnCallSchedule, OnCallInput>({
    mutationFn: (dto) =>
      apiPost<OnCallSchedule, OnCallInput>(operationsClient, schedulerEndpoints.oncalls(), dto),
    onSuccess: invalidate,
    showErrorToast: true,
  });
}

export function useUpdateOnCall() {
  const invalidate = useInvalidate();
  return useApiMutation<OnCallSchedule, { id: string; dto: Partial<OnCallInput> }>({
    mutationFn: ({ id, dto }) =>
      apiPut<OnCallSchedule, Partial<OnCallInput>>(
        operationsClient,
        schedulerEndpoints.oncall(id),
        dto,
      ),
    onSuccess: invalidate,
    showErrorToast: true,
  });
}

export function useDeleteOnCall() {
  const invalidate = useInvalidate();
  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, schedulerEndpoints.oncall(id)),
    onSuccess: invalidate,
    showErrorToast: true,
  });
}
